/**
 * Input Status Indicator Directive
 * Responsible for showing input level errors specific to a particular field
 *
 */
import { Directive, ElementRef, inject, input } from '@angular/core';

import { InputGroupFieldComponent } from '../input-group/input-group-field.component';
import { FormRowComponent } from '../row/form-row.component';
import { FormComponent } from '../form.component';
import type { Subscription } from 'rxjs';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import type { FormError } from '../domain/form.domain';

@Directive()
export abstract class BaseInputStatusDirective implements OnInit, AfterViewInit, OnDestroy {
  private static readonly ACCESSIBLE_ERROR_SUFFIX: string = '-accessible-error';

  readonly name = input<string>(undefined);
  readonly ariaDescribedBy = input<string>(undefined, { alias: 'tdWbInputAriaDescribedBy' });

  describedBy: string | undefined;
  ariaInvalid: string;
  private onErrorSubscription: Subscription;
  private hasError: boolean = false;

  private readonly elementRef = inject<ElementRef>(ElementRef);
  protected readonly inputGroupField = inject(InputGroupFieldComponent, { host: true, optional: true });
  protected readonly form = inject(FormComponent, { optional: true });
  protected readonly formRow = inject(FormRowComponent, { host: true, optional: true });

  ngOnInit(): void {
    // set aria attributes here to make sure that inputGroupField is not needed for accessibility function to work
    // need to put it here, as putting it inside of ngAfterViewInit causes ExpressionChangedAfterItHasBeenCheckedError
    this.setAriaAttributes();
  }

  ngAfterViewInit(): void {
    let inputControl;
    if ((this.inputGroupField ?? null) !== null) {
      inputControl = this.inputGroupField.getInputControl();
      this.onErrorSubscription = this.inputGroupField.onErrorSubject.subscribe((value: FormError): void => this.toggleError(value));
    }
    if (inputControl !== undefined) {
      if (this.hasForm()) {
        this.form.registerFormControl(inputControl);
      }
      if (this.hasFormRow()) {
        this.formRow.registerFormControl(inputControl);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.onErrorSubscription !== undefined) {
      this.onErrorSubscription.unsubscribe();
      const inputControl = this.inputGroupField.getInputControl();
      if (inputControl !== undefined) {
        if (this.hasForm()) {
          this.toggleError({ name: inputControl.name, value: false });
          this.form.unRegisterFormControl(inputControl);
        }
        if (this.hasFormRow()) {
          this.formRow.unRegisterFormControl(inputControl);
        }
      }
    }
  }

  // Public API
  isErrorState(): boolean {
    return this.hasError;
  }

  focus(): void {
    if (this.elementRef.nativeElement !== null &&  typeof this.elementRef.nativeElement.focus === 'function') {
      this.elementRef.nativeElement.focus();
    }
  }

  protected onErrorStateChange(): void {
    this.setAriaAttributes();
  }

  private hasForm(): boolean {
    return this.form !== undefined && this.form !== null;
  }

  private hasFormRow(): boolean {
    return this.formRow !== undefined && this.formRow !== null;
  }

  private toggleError(value: FormError): void {
    this.hasError = value.value;
    if (this.hasForm()) {
      const forceDisplay = this.inputGroupField.shouldForceDisplayError();
      if (this.hasError !== undefined) {
        const isSubmitted = this.inputGroupField.isSubmitted();
        if (isSubmitted || forceDisplay || !this.hasError) {
          this.onErrorStateChange();
        } else if (!isSubmitted && !forceDisplay && this.hasError) {
          this.hasError = false;
          value.value = this.hasError;
          this.onErrorStateChange();
        }
      } else if (this.inputGroupField.isSubmitted() || forceDisplay) {
        this.onErrorStateChange();
      }
    }
  }

  private setAriaAttributes(): void {
    const ariaDescribedBy = this.ariaDescribedBy();
    if (ariaDescribedBy !== undefined) {
      this.describedBy = ariaDescribedBy;
      if (this.hasError) {
        this.describedBy = this.describedBy + ' ' + this.name() + BaseInputStatusDirective.ACCESSIBLE_ERROR_SUFFIX;
      }
    } else {
      this.describedBy = this.hasError ? this.name() + BaseInputStatusDirective.ACCESSIBLE_ERROR_SUFFIX : undefined;
    }
    this.ariaInvalid = this.hasError.toString();
  }
}
